import type { MotionChildTCPOption } from '@sb/motion-planning';
import { isSymbolicTooltip } from '@sb/motion-planning';
import {
  type ConditionalArgument,
  describeStoredConditionsProblem,
} from '@sb/remote-control/types';
import { buildConditional } from '@sb/remote-control/util/conditionalsBuilder';
import { stepOutputNames } from '@sb/remote-control/util/stepOutputs';
import type { WaitForConfirmationModes } from '@sb/routine-runner';

import type { ValidationContext } from '../types';

import { ActionRequiredError, type WarningFinding } from './errors';
import { assertNotExamplePlaceholder } from './examplePlaceholder';
import { isTooltipSupplied } from './tooltipReference';

// Local implementations (ported) of Remote Control helpers

/**
 * Validate a code expression. A genuine expression error throws; a
 * deprecated-syntax finding is returned (not thrown) so the caller can
 * record it as a non-blocking warning and keep running its remaining
 * checks — a warning must never hide an error a later check would raise.
 */
export function validateCodeExpression(
  expression:
    | { kind?: string; errorMessage?: string | null }
    | undefined
    | null,
  fieldId?: string,
  name: string | undefined = fieldId,
): WarningFinding | undefined {
  if (
    (expression?.kind === 'JavaScript' || expression?.kind === 'Python') &&
    expression.errorMessage
  ) {
    if (fieldId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `Error in ${name}: ${expression.errorMessage}`,
        fieldId,
      });
    } else {
      throw new Error(`Error in expression: ${expression.errorMessage}`);
    }
  }

  if (expression?.kind === 'JavaScript') {
    return {
      kind: 'deprecatedConfigurationWarning',
      message: `Expression uses deprecated syntax.`,
      fieldId,
      deprecationKind: 'javaScriptExpressions',
    };
  }

  return undefined;
}

type Conditional = {
  variable?:
    | {
        kind: 'step';
        stepID: string;
        stepKind: string;
        variableName: string;
        stepPosition: number;
      }
    | { kind: 'expression' }
    | { kind: string }
    | null;
  value?: unknown;
};

/** The output a Prompt User step sets beside `confirmed`, by its mode. */
export const WAIT_FOR_CONFIRMATION_OUTPUT: Required<{
  [K in WaitForConfirmationModes]: string;
}> = {
  confirm: 'confirmed',
  choice: 'choice',
  freeform: 'freeformInput',
};

export function validateConditionalWaitForConfirmationStep(
  condition: Conditional,
  step: {
    stepKind: string;
    args?: { mode: WaitForConfirmationModes; choices: string[] };
  },
): void {
  if (!condition.variable || condition.variable.kind !== 'step') return;

  const { variableName, stepPosition } = condition.variable as any;

  // Rows written through the routine DSL store no stepPosition.
  const promptName =
    stepPosition == null ? 'Prompt User' : `${stepPosition}. Prompt User`;

  if (step.stepKind !== 'WaitForConfirmation' || step.args == null) {
    throw new Error(
      'Runtime Error: Cannot pass a step that is not `WaitForConfirmation` to this validator.',
    );
  }

  const { mode, choices } = step.args;

  if (WAIT_FOR_CONFIRMATION_OUTPUT[mode] !== variableName) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `Condition must be updated: Variable "${promptName}" is invalid. Update to reflect that the prompt is now of type "${mode}."`,
      fieldId: 'condition',
    });
  }

  if (
    mode === 'choice' &&
    !choices.includes((condition.value || '').toString())
  ) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `Condition must be updated: Value for variable "${promptName}" is invalid. Choice must be updated.`,
      fieldId: 'condition',
    });
  }
}

export function validateConditionalSteps(
  conditions: Conditional[][],
  routine: {
    stepConfigurations: Record<string, { stepKind: string; args?: any }>;
  },
): void {
  for (let i = 0; i < conditions.length; i += 1) {
    for (let j = 0; j < conditions[i].length; j += 1) {
      const condition = conditions[i][j];

      if (condition.variable?.kind !== 'step') continue;

      const stepVar = condition.variable as any;
      const step = routine.stepConfigurations[stepVar.stepID];

      if (!step) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Condition must be updated: Makes use of invalid step.',
          fieldId: 'condition',
        });
      }

      if (step.stepKind !== stepVar.stepKind) {
        throw new Error(
          `Conditional step validation failed: step's kind is different from condition's stepKind. [step.stepKind=${step.stepKind} !== condition.variable.stepKind (${stepVar.stepKind})].`,
        );
      }

      switch (stepVar.stepKind) {
        case 'WaitForConfirmation':
          validateConditionalWaitForConfirmationStep(condition, step as any);
          break;
        default: {
          const outputs = stepOutputNames(step.stepKind, step.args);

          if (!outputs.includes(stepVar.variableName)) {
            const produces =
              outputs.length === 0
                ? 'produces no outputs'
                : `produces ${outputs.map((name) => `"${name}"`).join(', ')}`;

            throw new ActionRequiredError({
              kind: 'invalidConfiguration',
              message: `Condition must be updated: "${stepVar.variableName}" is not an output of the ${step.stepKind} step it reads; that step ${produces}.`,
              fieldId: 'condition',
            });
          }

          break;
        }
      }
    }
  }
}

/**
 * Report deprecated JavaScript syntax in conditional expressions. Returns
 * the finding (rather than throwing) so callers can record it as a
 * non-blocking warning and keep running their remaining checks.
 */
export function validateConditionalJavascriptExpressions(
  conditions: Conditional[][],
): WarningFinding | undefined {
  for (let i = 0; i < conditions.length; i += 1) {
    for (let j = 0; j < conditions[i].length; j += 1) {
      const condition = conditions[i][j];

      if (condition.variable?.kind !== 'expression') continue;

      const value = condition?.value as any;

      if (value && typeof value === 'object' && value.kind === 'JavaScript') {
        return {
          kind: 'deprecatedConfigurationWarning',
          message: 'Condition uses deprecated syntax.',
          fieldId: 'condition',
          deprecationKind: 'javaScriptExpressions',
        };
      }
    }
  }

  return undefined;
}

/**
 * Validate a parsed conditional. Blocking problems throw; a deprecated
 * JavaScript expression is returned as a non-blocking warning.
 */
export function validateConditional(
  conditions: Conditional[][],
  routine: {
    stepConfigurations: Record<string, { stepKind: string; args?: any }>;
  },
): WarningFinding | undefined {
  validateConditionalSteps(conditions, routine);

  return validateConditionalJavascriptExpressions(conditions);
}

/**
 * Check a stored condition against the stored condition schema, which
 * `toRoutineRunner` applies before converting it; one that fails would pass
 * validation and then fail the run.
 *
 * @throws {ActionRequiredError} When the condition does not match the schema.
 */
export function validateStoredConditionShape(
  conditions: unknown,
  fieldId = 'condition',
): void {
  const problem = describeStoredConditionsProblem(conditions);

  if (problem) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `Condition is not valid: ${problem}.`,
      fieldId,
    });
  }
}

/**
 * Check that builder-shaped conditions actually convert to a runner
 * conditional.
 *
 * `validateConditional` only inspects the references a condition makes, so a
 * half-filled row - no operator, no variable, or no value - passes it and
 * fails later, while converting the routine for the runner. For the operator
 * that means Play stays enabled and the run dies on its first evaluation with
 * "Condition is not fully configured."
 *
 * A present-but-empty condition counts as incomplete too: an If/ElseIf with
 * nothing to compare cannot be evaluated either.
 *
 * @throws {ActionRequiredError} When the condition cannot be converted.
 */
export function validateConditionalIsComplete(conditions: unknown): void {
  const incomplete = () =>
    new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: 'Condition is not fully configured.',
      fieldId: 'condition',
    });

  if (!Array.isArray(conditions) || conditions.length === 0) {
    throw incomplete();
  }

  try {
    buildConditional(
      conditions as ConditionalArgument.ConditionalBuilderState[][],
    );
  } catch (err) {
    // `buildConditional` already throws an ActionRequiredError naming the
    // problem for every incomplete row; keep it. Anything else - a malformed
    // nesting that makes it read a property of undefined - would otherwise
    // surface as a raw TypeError, so report it the same way.
    if ((err as { actionRequired?: unknown })?.actionRequired) throw err;

    throw incomplete();
  }

  validateStoredConditionShape(conditions);
}

/**
 * Does this tooltip name resolve to nothing on this robot? Shared, so that
 * every surface naming a legacy tooltip judges it the same way and only the
 * message differs.
 */
export function isTooltipDangling({
  tcpOption,
  gripperID,
  ctx,
}: {
  tcpOption: MotionChildTCPOption | null | undefined;
  gripperID: string | undefined;
  ctx: ValidationContext;
}): boolean {
  // Nothing was named, so nothing can dangle: a symbolic member resolves to
  // whatever a target pose or the robot supplies rather than to a fixed
  // tooltip.
  if (tcpOption == null || isSymbolicTooltip(tcpOption)) return false;

  // The caller never supplied equipment, so this robot's tooltips are unknown.
  // `isTooltipSupplied` reads a missing list as "supplied by nothing", so
  // judging here would accuse every reference at once. An empty list is
  // different — it says the robot publishes none, so a named one is dangling.
  if (ctx?.equipment == null) return false;

  return !isTooltipSupplied({
    tcpOption,
    gripperID,
    equipment: ctx.equipment,
  });
}

/**
 * A tooltip a motion child names outright must still be supplied by this
 * robot. One body for waypoints and arcs — the two read and write the same
 * legacy fields, so the check must never drift between them.
 *
 * Runs on every robot, because a name nothing publishes moves the arm in the
 * wrong frame whatever the robot's configuration: device kinematics fall back
 * to their own offset and the motion is silently taught against a different
 * point. `tcpOption` accepts any `ee-`-prefixed string, so this is the only
 * layer that can tell the author the name resolves to nothing.
 *
 * Returned rather than thrown: a throw would end the step's remaining checks,
 * and the runner-schema cross-check runs outside the sequence a call site can
 * reorder, so only the caller's `warn()` collector can keep it reachable.
 *
 * `danglingTooltip` rather than `missingConfiguration`: the configuration is
 * present and complete, and the name it holds resolves to nothing on this
 * robot.
 *
 * Warns rather than blocks: `tcpOption` predates the Active Tooltip work, so a
 * routine authored before it can already carry a dangling one, and already ran
 * that way. Blocking would stop a routine that plays today — the author is
 * told, and decides. Returning it rather than throwing is what puts it at the
 * warning tier, so it never fails the precheck for those routines.
 *
 * The one exception blocks: a `gripperID` that is still an example-routine
 * placeholder. Unlike a dangling legacy tooltip, no routine ever ran with
 * one, so there is nothing a warning would protect.
 *
 * @returns a non-blocking warning, or `undefined`.
 * @throws {ActionRequiredError} on an example-placeholder `gripperID`.
 */
export function validateLegacyTooltipSupplied({
  stepNoun,
  tcpOption,
  gripperID,
  ctx,
}: {
  /** Names the step in the message: `waypoint` or `arc`. */
  stepNoun: string;
  tcpOption: MotionChildTCPOption | null | undefined;
  gripperID: string | undefined;
  ctx: ValidationContext;
}): WarningFinding | undefined {
  assertNotExamplePlaceholder(gripperID, 'tcpOption');

  if (!isTooltipDangling({ tcpOption, gripperID, ctx })) return undefined;

  return {
    kind: 'danglingTooltip',
    message: `This ${stepNoun}'s tooltip isn't on this robot any more. It will move at the wrist flange unless you choose another.`,
    fieldId: 'tcpOption',
  };
}

export function validateMotionSpeed(
  stepKind: string,
  motionSpeed?: { customLimits?: unknown },
): void {
  const customLimits = motionSpeed?.customLimits as
    | Record<string, unknown>
    | null
    | undefined;

  if (
    customLimits?.jointAccelerationsPercent ||
    customLimits?.jointSpeedsPercent ||
    customLimits?.tooltipSpeedPercent
  ) {
    throw new Error(
      `${stepKind} step is configured with motion speed custom limits with an outdated configuration.
    Please reconfigure ${stepKind} steps with custom limit speeds.`,
    );
  }
}
